import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import {
  Clock,
  Ticket,
  AlertTriangle,
  CheckCircle2,
  Search,
  RefreshCw,
  Send,
  Calendar,
  Plane,
  Users,
  Mail,
  Timer,
  XCircle,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * TTL Management Panel - Admin component for managing ticket time limits
 */
export const TTLManagementPanel = () => {
  const [searchPnr, setSearchPnr] = useState('');
  const [booking, setBooking] = useState(null);
  const [ttlStatus, setTtlStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // TTL Setting form
  const [newTtlDate, setNewTtlDate] = useState('');
  const [newTtlTime, setNewTtlTime] = useState('');
  const [isSettingTtl, setIsSettingTtl] = useState(false);
  
  // Ticketing form
  const [ticketNumber, setTicketNumber] = useState('');
  const [isTicketing, setIsTicketing] = useState(false);
  
  // Check reminders
  const [isCheckingReminders, setIsCheckingReminders] = useState(false);

  const searchBooking = async () => {
    if (!searchPnr.trim()) {
      setError('Please enter a PNR');
      return;
    }
    
    setIsLoading(true);
    setError('');
    setSuccess('');
    setBooking(null);
    setTtlStatus(null);
    
    try {
      // Get booking details
      const bookingRes = await axios.get(`${BACKEND_URL}/api/bookings/${searchPnr.toUpperCase()}`);
      if (bookingRes.data.success) {
        setBooking(bookingRes.data.booking);
      } else {
        setError(bookingRes.data.message || 'Booking not found');
        return;
      }
      
      // Get TTL status
      const ttlRes = await axios.get(`${BACKEND_URL}/api/bookings/${searchPnr.toUpperCase()}/ttl-status`);
      setTtlStatus(ttlRes.data);
      
    } catch (err) {
      console.error('Search error:', err);
      setError(err.response?.data?.detail || 'Booking not found');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetTtl = async () => {
    if (!newTtlDate || !newTtlTime) {
      setError('Please select both date and time for TTL');
      return;
    }
    
    setIsSettingTtl(true);
    setError('');
    setSuccess('');
    
    try {
      const ttlDatetime = new Date(`${newTtlDate}T${newTtlTime}:00`).toISOString();
      
      const response = await axios.post(`${BACKEND_URL}/api/bookings/set-ttl`, {
        pnr: searchPnr.toUpperCase(),
        ticket_time_limit_at: ttlDatetime
      });
      
      setSuccess(response.data.message);
      
      // Refresh TTL status
      const ttlRes = await axios.get(`${BACKEND_URL}/api/bookings/${searchPnr.toUpperCase()}/ttl-status`);
      setTtlStatus(ttlRes.data);
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to set TTL');
    } finally {
      setIsSettingTtl(false);
    }
  };

  const handleTicket = async () => {
    if (!ticketNumber.trim()) {
      setError('Please enter a ticket number');
      return;
    }
    
    setIsTicketing(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/bookings/ticket`, {
        pnr: searchPnr.toUpperCase(),
        ticket_number: ticketNumber
      });
      
      setSuccess(response.data.message);
      
      // Refresh TTL status
      const ttlRes = await axios.get(`${BACKEND_URL}/api/bookings/${searchPnr.toUpperCase()}/ttl-status`);
      setTtlStatus(ttlRes.data);
      
      // Refresh booking
      const bookingRes = await axios.get(`${BACKEND_URL}/api/bookings/${searchPnr.toUpperCase()}`);
      if (bookingRes.data.success) {
        setBooking(bookingRes.data.booking);
      }
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to ticket booking');
    } finally {
      setIsTicketing(false);
    }
  };

  const handleCheckReminders = async () => {
    setIsCheckingReminders(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/bookings/check-ttl-reminders`);
      setSuccess('TTL reminder check completed - emails sent to bookings within 12 hours of deadline');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to check reminders');
    } finally {
      setIsCheckingReminders(false);
    }
  };

  const getStatusBadge = () => {
    if (!ttlStatus) return null;
    
    if (ttlStatus.is_ticketed) {
      return <Badge className="bg-green-500 text-white">TICKETED</Badge>;
    }
    
    if (ttlStatus.time_remaining === 'EXPIRED') {
      return <Badge className="bg-red-500 text-white">TTL EXPIRED</Badge>;
    }
    
    if (ttlStatus.hours_remaining && ttlStatus.hours_remaining < 6) {
      return <Badge className="bg-red-500 text-white animate-pulse">URGENT - {ttlStatus.time_remaining}</Badge>;
    }
    
    if (ttlStatus.hours_remaining && ttlStatus.hours_remaining < 12) {
      return <Badge className="bg-amber-500 text-white">WARNING - {ttlStatus.time_remaining}</Badge>;
    }
    
    if (ttlStatus.ticket_time_limit_at) {
      return <Badge className="bg-blue-500 text-white">{ttlStatus.time_remaining} remaining</Badge>;
    }
    
    return <Badge variant="outline">No TTL Set</Badge>;
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    try {
      return new Date(isoString).toLocaleString('en-GB', {
        dateStyle: 'medium',
        timeStyle: 'short'
      });
    } catch {
      return isoString;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Timer className="h-6 w-6 text-brand-500" />
            TTL Management
          </h2>
          <p className="text-slate-500 text-sm mt-1">Manage ticket time limits and send reminders</p>
        </div>
        <Button 
          variant="outline" 
          onClick={handleCheckReminders}
          disabled={isCheckingReminders}
          className="border-amber-500 text-amber-600 hover:bg-amber-50"
        >
          {isCheckingReminders ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Send className="h-4 w-4 mr-2" />
          )}
          Trigger TTL Check
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <Label htmlFor="search-pnr">Search by PNR</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  id="search-pnr"
                  placeholder="Enter PNR (e.g., UROFOL)"
                  value={searchPnr}
                  onChange={(e) => setSearchPnr(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === 'Enter' && searchBooking()}
                  className="font-mono"
                  data-testid="ttl-search-pnr"
                />
                <Button onClick={searchBooking} disabled={isLoading}>
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
          
          {/* Messages */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <XCircle className="h-4 w-4" />
              {error}
            </div>
          )}
          {success && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
              <CheckCircle2 className="h-4 w-4" />
              {success}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Booking Details */}
      {booking && ttlStatus && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Booking Info */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Plane className="h-5 w-5 text-blue-500" />
                  Booking Details
                </CardTitle>
                {getStatusBadge()}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* PNR */}
              <div className="bg-slate-900 text-white p-4 rounded-lg text-center">
                <p className="text-xs text-slate-400 uppercase">Booking Reference</p>
                <p className="text-3xl font-bold font-mono tracking-wider">{booking.pnr}</p>
              </div>
              
              {/* Flight Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Route</p>
                  <p className="font-semibold">{booking.flight_data?.from || booking.flight?.from} → {booking.flight_data?.to || booking.flight?.to}</p>
                </div>
                <div>
                  <p className="text-slate-500">Departure</p>
                  <p className="font-semibold">{booking.flight_data?.departure_date || booking.flight?.departure_date}</p>
                </div>
                <div>
                  <p className="text-slate-500">Airline</p>
                  <p className="font-semibold">{booking.flight_data?.airline || booking.flight?.airline || booking.flight_data?.airline_code}</p>
                </div>
                <div>
                  <p className="text-slate-500">Passengers</p>
                  <p className="font-semibold">
                    {booking.passengers?.length || 0}
                    {booking.passenger_counts && (
                      <span className="text-xs text-slate-400 ml-1">
                        ({[
                          booking.passenger_counts.adults > 0 && `${booking.passenger_counts.adults} ADT`,
                          booking.passenger_counts.youth > 0 && `${booking.passenger_counts.youth} YTH`,
                          booking.passenger_counts.children > 0 && `${booking.passenger_counts.children} CHD`,
                          booking.passenger_counts.infants > 0 && `${booking.passenger_counts.infants} INF`
                        ].filter(Boolean).join(', ')})
                      </span>
                    )}
                  </p>
                </div>
              </div>
              
              <Separator />
              
              {/* TTL Info */}
              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <Clock className="h-4 w-4 text-amber-500" />
                  TTL Information
                </h4>
                
                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Time Limit:</span>
                    <span className="font-medium">{formatDateTime(ttlStatus.ticket_time_limit_at)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Time Remaining:</span>
                    <span className={`font-bold ${
                      ttlStatus.hours_remaining < 6 ? 'text-red-600' :
                      ttlStatus.hours_remaining < 12 ? 'text-amber-600' : 'text-green-600'
                    }`}>
                      {ttlStatus.time_remaining || 'No TTL set'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Ticket Number:</span>
                    <span className="font-medium font-mono">{ttlStatus.ticket_number || 'Not ticketed'}</span>
                  </div>
                </div>
                
                {/* Email Status */}
                <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-semibold text-slate-600 uppercase">Email Notifications</p>
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-slate-400" />
                    <span className="text-slate-500">TTL Generated:</span>
                    <span className={ttlStatus.ttl_email_sent_at ? 'text-green-600' : 'text-slate-400'}>
                      {ttlStatus.ttl_email_sent_at ? formatDateTime(ttlStatus.ttl_email_sent_at) : 'Not sent'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <AlertTriangle className="h-4 w-4 text-slate-400" />
                    <span className="text-slate-500">Last Reminder:</span>
                    <span className={ttlStatus.last_ttl_reminder_sent_at ? 'text-amber-600' : 'text-slate-400'}>
                      {ttlStatus.last_ttl_reminder_sent_at ? formatDateTime(ttlStatus.last_ttl_reminder_sent_at) : 'Not sent'}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Right Column - Actions */}
          <div className="space-y-6">
            {/* Set TTL Card */}
            {!ttlStatus.is_ticketed && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-purple-500" />
                    Set Ticket Time Limit
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="ttl-date">Date</Label>
                      <Input
                        id="ttl-date"
                        type="date"
                        value={newTtlDate}
                        onChange={(e) => setNewTtlDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="ttl-time">Time</Label>
                      <Input
                        id="ttl-time"
                        type="time"
                        value={newTtlTime}
                        onChange={(e) => setNewTtlTime(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <Button 
                    onClick={handleSetTtl} 
                    disabled={isSettingTtl || !newTtlDate || !newTtlTime}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    {isSettingTtl ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Clock className="h-4 w-4 mr-2" />
                    )}
                    Set TTL & Send Notification
                  </Button>
                  <p className="text-xs text-slate-500">
                    Setting a TTL will automatically send an email notification to info@flight380.co.uk
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Ticket Booking Card */}
            {!ttlStatus.is_ticketed && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Ticket className="h-5 w-5 text-green-500" />
                    Issue Ticket
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="ticket-number">Ticket Number</Label>
                    <Input
                      id="ticket-number"
                      placeholder="e.g., 176-1234567890"
                      value={ticketNumber}
                      onChange={(e) => setTicketNumber(e.target.value)}
                      className="mt-1 font-mono"
                    />
                  </div>
                  <Button 
                    onClick={handleTicket} 
                    disabled={isTicketing || !ticketNumber.trim()}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    {isTicketing ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                    )}
                    Mark as Ticketed
                  </Button>
                  <p className="text-xs text-slate-500">
                    Marking as ticketed will stop all TTL reminder emails for this booking
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Already Ticketed */}
            {ttlStatus.is_ticketed && (
              <Card className="border-green-200 bg-green-50">
                <CardContent className="pt-6">
                  <div className="text-center space-y-3">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                      <CheckCircle2 className="h-8 w-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-green-800">Booking Ticketed</h3>
                    <p className="text-green-700">
                      Ticket Number: <span className="font-mono font-bold">{ttlStatus.ticket_number}</span>
                    </p>
                    <p className="text-sm text-green-600">
                      No further TTL reminders will be sent for this booking
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TTLManagementPanel;
